import {
  getGetCaseByNumberMockHandler,
  getGetCasesMockHandler,
} from "@vega-api/generated/case-management-v1/case-management-v1.msw";
import { getGetInvestorContextMockHandler } from "@vega-api/generated/investor-context-v1/investor-context-v1.msw";
import { type ApiCaseListResponse } from "@vega-api/generated/types/apiCaseListResponse";
import { type ApiCaseResponse } from "@vega-api/generated/types/apiCaseResponse";
import { type ApiInvestorContextResponse } from "@vega-api/generated/types/apiInvestorContextResponse";

/**
 * Browser-dev-only Cases dataset (yarn msw:start:gp). Registered in
 * demoOnlyHandlers so it shadows the customHandlers cases mocks in the dev
 * worker without reaching the f2f playwright-msw worker (which builds from
 * customHandlers only). Purpose: enough open cases to exercise pagination and
 * the fixed-height inbox, plus a populated investor context so the case-detail
 * sidebar shows real investor data (and its long-email truncation).
 */

const DEMO_STATUSES = ["NEW", "IN_PROGRESS", "RESOLVED", "CLOSED"] as const;
const DEMO_PRIORITIES = ["HIGH", "MEDIUM", "LOW"] as const;

const CATEGORY_TECH = {
  id: "123e4567-e89b-12d3-a456-426614174012",
  name: "Technical Issues",
};
const CATEGORY_INVESTOR = {
  id: "223e4567-e89b-12d3-a456-426614174012",
  name: "Investor Queries",
};

const DEMO_TITLES = [
  "Request to change wire instructions",
  "Dashboard loading issue",
  "Quarterly report request",
  "Question about the Q2 statement",
  "Capital call payment confirmation",
  "Update authorised signatory",
  "Missing distribution notice",
  "Tax document access request",
  "Fund performance clarification",
  "Subscription document re-send",
  "Bank details verification",
  "Portal login not working",
  "Commitment amount discrepancy",
  "Request for K-1 statement",
  "Change of registered address",
  "NAV statement discrepancy",
] as const;

const DEMO_EMAILS = [
  "user@example.com",
  "knox.pope@familyoffice.com",
  "meridian@pensiontrust.com",
  "au2316+test@columbia.edu",
  "operations@evergreen-capital-partners.example.com",
  "j.smith@acme.com",
] as const;

const isoDaysAgo = (days: number) =>
  new Date(Date.UTC(2026, 6, 24) - days * 86_400_000).toISOString();

const DEMO_CASES = DEMO_TITLES.map((title, index) => {
  const status = DEMO_STATUSES[index % 3 === 0 ? 3 : index % 2];
  const openStatus = index < 13 ? DEMO_STATUSES[index % 2] : status;

  return {
    caseId: `demo-case-${index + 1}`,
    caseNumber: `APO-00${1234 + index}`,
    caseCategoryId: index % 2 === 0 ? CATEGORY_TECH.id : CATEGORY_INVESTOR.id,
    caseCategoryName:
      index % 2 === 0 ? CATEGORY_TECH.name : CATEGORY_INVESTOR.name,
    caseTypeId: "case-type-2",
    caseTypeName: "Performance Issue",
    title,
    description: `${title} — raised by the LP; awaiting operator triage.`,
    assignee: index % 3 === 0 ? "user-1" : undefined,
    assigneeGroup: index % 3 === 0 ? "ABC team" : undefined,
    status: openStatus,
    priority: DEMO_PRIORITIES[index % 3],
    emailAddress: DEMO_EMAILS[index % DEMO_EMAILS.length],
    slaInHours: 24 + (index % 3) * 24,
    slaBreachedTimestamp: index % 4 === 0 ? isoDaysAgo(1) : undefined,
    createdAt: isoDaysAgo(index + 1),
    updatedAt: isoDaysAgo(index),
  };
}) satisfies NonNullable<ApiCaseListResponse["data"]>["cases"];

const readListParams = (url: URL, name: string) =>
  [...url.searchParams.getAll(name), ...url.searchParams.getAll(`${name}[]`)]
    .flatMap((value) => value.split(","))
    .filter(Boolean);

const filterDemoCases = (url: URL) => {
  const statuses = readListParams(url, "status");
  const assignees = readListParams(url, "assignee");
  const priorities = readListParams(url, "priority");
  const categories = readListParams(url, "category");
  const searchQuery = url.searchParams.get("searchQuery")?.toLowerCase();
  const overdue = url.searchParams.get("overdue") === "true";

  let cases: typeof DEMO_CASES = DEMO_CASES;

  if (overdue) {
    cases = cases.filter((demoCase) => Boolean(demoCase.slaBreachedTimestamp));
  }

  if (statuses.length > 0) {
    cases = cases.filter((demoCase) => statuses.includes(demoCase.status));
  }

  if (priorities.length > 0) {
    cases = cases.filter((demoCase) => priorities.includes(demoCase.priority));
  }

  if (categories.length > 0) {
    cases = cases.filter((demoCase) =>
      categories.includes(demoCase.caseCategoryId),
    );
  }

  if (assignees.length > 0) {
    cases = cases.filter(
      (demoCase) =>
        demoCase.assignee !== undefined &&
        assignees.includes(demoCase.assignee),
    );
  }

  if (searchQuery !== undefined && searchQuery !== "") {
    cases = cases.filter((demoCase) =>
      `${demoCase.caseNumber} ${demoCase.title} ${demoCase.description} ${demoCase.emailAddress} ${demoCase.caseCategoryName}`
        .toLowerCase()
        .includes(searchQuery),
    );
  }

  return cases;
};

const buildPaginatedCasesResponse = (info: {
  request: Request;
}): ApiCaseListResponse => {
  const url = new URL(info.request.url);
  const filtered = filterDemoCases(url);

  const page = Math.max(Number(url.searchParams.get("page")) || 1, 1);
  const pageSize = Math.max(Number(url.searchParams.get("pageSize")) || 10, 1);
  const totalPages = Math.max(Math.ceil(filtered.length / pageSize), 1);
  const start = (page - 1) * pageSize;
  const pageCases = filtered.slice(start, start + pageSize);

  return {
    data: { cases: pageCases },
    meta: {
      count: filtered.length,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
    },
    errors: {},
  };
};

const DEMO_INVESTOR_ID = "55555555-5555-5555-5555-555555555555";
const DEMO_LP_FIRM_ID = "44444444-4444-4444-4444-444444444444";

const buildDemoInvestorContext = (): ApiInvestorContextResponse => ({
  data: {
    available: true,
    lpFirmId: DEMO_LP_FIRM_ID,
    investor: {
      id: DEMO_INVESTOR_ID,
      name: "Alexandra Understanding-Vazquez",
      email: "au2316+test@columbia.edu",
      phone: "+1 (212) 555-0182",
      role: "Authorised signatory",
    },
    contacts: [],
    currentSubscriptions: [
      {
        subscriptionId: "sub-demo-1",
        fundId: "fund-demo-1",
        fundName: "Evergreen Capital Partners Fund III",
        shareClass: "Class A",
        committedAmount: { amount: 5_000_000, currency: "USD" },
        status: "ACTIVE",
      },
      {
        subscriptionId: "sub-demo-2",
        fundId: "fund-demo-2",
        fundName: "Evergreen Growth Opportunities",
        shareClass: "Class B",
        committedAmount: { amount: 2_500_000, currency: "USD" },
        status: "PENDING",
      },
    ],
    capitalCallActivity: [
      {
        capitalActivityId: "cap-demo-1",
        type: "CAPITAL_CALL",
        amount: { amount: 750_000, currency: "USD" },
        dueDate: isoDaysAgo(-14),
        status: "UPCOMING",
        reference: "Call #4 — Fund III",
      },
      {
        capitalActivityId: "cap-demo-2",
        type: "NET_CAPITAL_CALL",
        amount: { amount: 500_000, currency: "USD" },
        dueDate: isoDaysAgo(30),
        status: "PAST",
        reference: "Call #3 — Fund III",
      },
    ],
    previousCases: [
      {
        caseId: "demo-case-prev-1",
        caseNumber: "APO-001200",
        title: "Prior wire instruction update",
        status: "CLOSED",
        createdAt: isoDaysAgo(90),
      },
    ],
  },
  meta: {},
  errors: {},
});

export const getCasesDemoHandler = () =>
  getGetCasesMockHandler(buildPaginatedCasesResponse);

export const getCaseByNumberDemoHandler = () =>
  getGetCaseByNumberMockHandler((info) => {
    const caseNumber = info.params.caseNumber as string;
    const match = DEMO_CASES.find(
      (demoCase) => demoCase.caseNumber === caseNumber,
    );
    const source = match ?? DEMO_CASES[0];

    const response: ApiCaseResponse = {
      data: { ...source, caseNumber },
      meta: {},
      errors: {},
    };

    return response;
  });

export const getInvestorContextDemoHandler = () =>
  getGetInvestorContextMockHandler(() => buildDemoInvestorContext());
